import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="state" role="status">
      <p className="state-title">{title}</p>
      {children ? <p>{children}</p> : null}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : "Something went wrong.";
  return (
    <div className="state error" role="alert">
      <p className="state-title">This couldn't be loaded</p>
      <p>{message}</p>
      {onRetry ? <button className="btn btn-sm" onClick={onRetry}>Retry</button> : null}
    </div>
  );
}

export function Skeleton({ height = 16, width = "100%", style }: { height?: number | string; width?: number | string; style?: React.CSSProperties }) {
  return <div className="skeleton" style={{ height, width, ...style }} aria-hidden="true" />;
}

export function SkeletonBlock({ rows = 4, height = 18 }: { rows?: number; height?: number }) {
  return (
    <div className="stack" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} height={height} width={`${100 - (i % 3) * 12}%`} />)}
    </div>
  );
}

/** Renders loading / error for a query and hands the data to children once it is there. */
export function QueryState<T>({
  query, children, skeleton,
}: { query: UseQueryResult<T>; children: (data: T) => ReactNode; skeleton?: ReactNode }) {
  if (query.isPending) return <>{skeleton ?? <SkeletonBlock />}</>;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  return <>{children(query.data as T)}</>;
}
